/**
 * Cart Recommendations - Custom Element for Cart Drawer
 * Loads recommendations when cart drawer opens
 */

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', class CartDrawerRecommendations extends HTMLElement {
    constructor() {
      super();
      this.container = null;
      this.cartProductIds = [];
      this.productsData = {};
      this.initialized = false;
      this.observer = null;
    }

    connectedCallback() {
      // Try to find container immediately
      this.ensureContainer();
      
      // Setup event listeners regardless
      this.setupEventListeners();
      
      // Setup MutationObserver to watch for container appearing
      this.setupContainerObserver();
      
      // If we have container, load immediately
      if (this.container) {
        setTimeout(() => this.loadRecommendations(), 100);
      }
    }
    
    setupContainerObserver() {
      // If container already exists, no need to observe
      if (this.container) return;
      
      // Watch for changes in this element's children
      this.observer = new MutationObserver((mutations) => {
        if (!this.container) {
          const found = this.ensureContainer();
          if (found) {
            this.observer.disconnect();
            this.observer = null;
            setTimeout(() => this.loadRecommendations(), 100);
          }
        }
      });
      
      // Start observing
      this.observer.observe(this, {
        childList: true,
        subtree: true
      });
    }
    
    disconnectedCallback() {
      // Clean up observer when element is removed
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
    
    ensureContainer() {
      if (!this.container) {
        this.container = this.querySelector('[data-recommendations-container]');
      }
      return this.container;
    }
    
    setupEventListeners() {
      if (this.initialized) return;
      this.initialized = true;
      
      // Listen for cart refresh events
      document.addEventListener('cart:refresh', () => {
        if (this.ensureContainer()) {
          this.loadRecommendations();
        }
      });
      
      // Subscribe to Minimog theme events
      if (window.MinimogEvents && window.MinimogTheme) {
        // Only reload if update came from another source
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, (event) => {
          if (event && event.source === 'cart-recommendations') {
            return;
          }
          if (this.ensureContainer()) {
            setTimeout(() => this.loadRecommendations(), 500);
          }
        });
        
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.openCartDrawer, () => {
          if (this.ensureContainer()) {
            setTimeout(() => this.loadRecommendations(), 300);
          }
        });
      }
    }

    async loadRecommendations() {
      if (!this.container) {
        return;
      }
      
      try {
        // Show loading spinner
        this.container.innerHTML = `
          <div class="m-cart-recommendations__loading" style="text-align: center; padding: 20px;">
            <svg class="m-spinner" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="animation: spin 1s linear infinite;">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2" opacity="0.25"/>
              <path fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z"/>
            </svg>
          </div>
        `;
        
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        if (!cart.items || cart.items.length === 0) {
          this.style.display = 'none';
          return;
        }
        
        this.style.display = 'block';
        this.cartProductIds = cart.items.map(item => item.product_id);
        
        const firstProductId = cart.items[0].product_id;
        
        const recsResponse = await fetch(`/recommendations/products.json?product_id=${firstProductId}&limit=10`);
        const data = await recsResponse.json();
        
        if (data.products && data.products.length > 0) {
          this.renderProducts(data.products, cart.currency);
        } else {
          await this.fetchCollectionProducts(cart.currency);
        }
      } catch (error) {
        console.error('Error loading recommendations:', error);
        // Try fallback with currency
        try {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          if (cart && cart.currency) {
            await this.fetchCollectionProducts(cart.currency);
          } else {
            this.style.display = 'none';
          }
        } catch (fallbackError) {
          console.error('Fallback failed:', fallbackError);
          this.style.display = 'none';
        }
      }
    }

    async fetchCollectionProducts(currency) {
      try {
        const response = await fetch('/collections/all/products.json?limit=12');
        const data = await response.json();
        if (data.products && data.products.length > 0) {
          this.renderProducts(data.products, currency);
        } else {
          this.style.display = 'none';
        }
      } catch (error) {
        console.error('Error fetching collection products:', error);
        this.style.display = 'none';
      }
    }

    renderProducts(products, currency) {
      const filteredProducts = products.filter(product => 
        !this.cartProductIds.includes(product.id)
      ).slice(0, 8);
      
      if (filteredProducts.length === 0) {
        this.style.display = 'none';
        return;
      }
      
      this.productsData = {};
      
      const productsHTML = filteredProducts.map(product => {
        const variant = product.variants[0];
        const imageUrl = product.featured_image 
          ? product.featured_image.replace('.jpg', '_400x.jpg').replace('.png', '_400x.png')
          : '';
        
        const price = this.formatMoney(variant.price, currency);
        const comparePrice = variant.compare_at_price 
          ? this.formatMoney(variant.compare_at_price, currency)
          : null;
        
        const onSale = comparePrice && variant.compare_at_price > variant.price;
        const available = variant.available;
        
        this.productsData[product.id] = {
          variants: product.variants,
          options: product.options || [],
          handle: product.handle,
          title: product.title
        };
        
        let sizeOptionsHTML = '';
        const sizeOption = product.options ? product.options.find(opt => 
          opt.name && (opt.name.toLowerCase().includes('size') || opt.name.toLowerCase().includes('sizes'))
        ) : null;
        
        if (sizeOption && product.variants.length > 1) {
          const sizePosition = sizeOption.position - 1;
          const uniqueSizes = [...new Set(product.variants.map(v => v.options[sizePosition]))];
          
          sizeOptionsHTML = `
            <div class="m-cart-recommendations__size-selector">
              <label class="m-cart-recommendations__size-label">${sizeOption.name}:</label>
              <div class="m-cart-recommendations__size-options" data-product-id="${product.id}">
                ${uniqueSizes.map((size, idx) => {
                  const sizeVariant = product.variants.find(v => v.options[sizePosition] === size);
                  const isAvailable = sizeVariant && sizeVariant.available;
                  return `
                    <button 
                      class="m-cart-recommendations__size-option ${idx === 0 ? 'selected' : ''}" 
                      data-size="${size}"
                      data-variant-id="${sizeVariant ? sizeVariant.id : ''}"
                      data-product-id="${product.id}"
                      ${!isAvailable ? 'disabled' : ''}
                      type="button"
                    >
                      ${size}
                    </button>
                  `;
                }).join('')}
              </div>
            </div>
          `;
        }
        
        return `
          <div class="m-cart-recommendations__item" data-product-id="${product.id}">
            <a href="/products/${product.handle}" class="m-cart-recommendations__image-link">
              <div class="m-cart-recommendations__image">
                ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : '<div class="m-cart-recommendations__image-placeholder"></div>'}
                ${onSale ? '<span class="m-cart-recommendations__badge">Sale</span>' : ''}
              </div>
            </a>
            <div class="m-cart-recommendations__info">
              <a href="/products/${product.handle}" class="m-cart-recommendations__title-link">
                <p class="m-cart-recommendations__product-title">${product.title}</p>
              </a>
              <div class="m-cart-recommendations__price">
                ${onSale 
                  ? `<span class="m-cart-recommendations__price--sale">${price}</span>
                     <span class="m-cart-recommendations__price--compare">${comparePrice}</span>`
                  : `<span>${price}</span>`
                }
              </div>
              ${sizeOptionsHTML}
              <button 
                class="m-cart-recommendations__add-btn" 
                data-variant-id="${variant.id}"
                data-product-id="${product.id}"
                ${!available ? 'disabled' : ''}
                type="button"
              >
                ${available ? 'Add to Cart' : 'Sold Out'}
              </button>
            </div>
          </div>
        `;
      }).join('');
      
      this.container.innerHTML = productsHTML;
      
      this.attachSizeSelectors();
      
      this.container.querySelectorAll('.m-cart-recommendations__add-btn').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleAddToCart(e));
      });
      
      setTimeout(() => this.initCarousel(), 100);
    }

    attachSizeSelectors() {
      const sizeButtons = this.container.querySelectorAll('.m-cart-recommendations__size-option');
      sizeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const productId = btn.dataset.productId;
          const variantId = btn.dataset.variantId;
          const productCard = btn.closest('.m-cart-recommendations__item');
          const addBtn = productCard.querySelector('.m-cart-recommendations__add-btn');
          const container = btn.closest('.m-cart-recommendations__size-options');
          
          container.querySelectorAll('.m-cart-recommendations__size-option').forEach(b => 
            b.classList.remove('selected')
          );
          btn.classList.add('selected');
          
          if (variantId) {
            addBtn.dataset.variantId = variantId;
            addBtn.disabled = btn.disabled;
          }
        });
      });
    }

    async handleAddToCart(e) {
      const btn = e.target;
      const variantId = btn.dataset.variantId;
      const originalText = btn.textContent;
      
      if (!variantId) {
        btn.classList.add('m-cart-recommendations__add-btn--error');
        btn.textContent = 'Select a size';
        setTimeout(() => {
          btn.classList.remove('m-cart-recommendations__add-btn--error');
          btn.textContent = originalText;
        }, 2000);
        return;
      }
      
      btn.disabled = true;
      btn.textContent = 'Adding...';
      btn.classList.add('m-cart-recommendations__add-btn--loading');
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ 
            items: [{ 
              id: parseInt(variantId), 
              quantity: 1 
            }] 
          })
        });
        
        const data = await response.json();
        
        if (response.ok && data.items) {
          btn.classList.remove('m-cart-recommendations__add-btn--loading');
          btn.classList.add('m-cart-recommendations__add-btn--success');
          btn.textContent = '✓ Added';
          
          // Trigger cart drawer refresh using the proper method
          const cartDrawer = document.querySelector('m-cart-drawer');
          if (cartDrawer && typeof cartDrawer.onCartDrawerUpdate === 'function') {
            cartDrawer.onCartDrawerUpdate();
          }
          
          // Also emit the cart update event for other listeners
          if (window.MinimogEvents && window.MinimogTheme) {
            MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
              source: 'cart-recommendations'
            });
          }
          
          setTimeout(() => {
            btn.disabled = false;
            btn.classList.remove('m-cart-recommendations__add-btn--success');
            btn.textContent = originalText;
          }, 2000);
        } else {
          throw new Error(data.message || data.description || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        btn.disabled = false;
        btn.classList.remove('m-cart-recommendations__add-btn--loading');
        btn.classList.add('m-cart-recommendations__add-btn--error');
        btn.textContent = error.message || 'Error';
        
        setTimeout(() => {
          btn.classList.remove('m-cart-recommendations__add-btn--error');
          btn.textContent = originalText;
        }, 2000);
      }
    }

    initCarousel() {
      const carousel = this.container;
      const prevBtn = this.querySelector('.m-cart-recommendations__nav-prev');
      const nextBtn = this.querySelector('.m-cart-recommendations__nav-next');
      
      if (!carousel || !prevBtn || !nextBtn) return;
      
      const updateButtons = () => {
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        prevBtn.disabled = carousel.scrollLeft <= 1;
        nextBtn.disabled = carousel.scrollLeft >= maxScroll - 1;
      };
      
      const scrollAmount = () => {
        const card = carousel.querySelector('.m-cart-recommendations__item');
        return card ? (card.offsetWidth + 12) * 2 : carousel.offsetWidth;
      };
      
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        carousel.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
        setTimeout(updateButtons, 100);
      });
      
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        carousel.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
        setTimeout(updateButtons, 100);
      });
      
      carousel.addEventListener('scroll', updateButtons);
      setTimeout(updateButtons, 200);
    }

    formatMoney(cents, currency) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        const format = window.MinimogSettings?.money_format || '{{amount}}';
        return Shopify.formatMoney(cents, format);
      }
      const amount = (cents / 100).toFixed(2);
      const symbols = { 'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AUD': 'A$', 'CAD': 'C$' };
      return `${symbols[currency] || currency}${amount}`;
    }
  });
}
